import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import '../../../css/accounts/reports/ConsolidatedReport.css';

const ConsolidatedReport = () => {
  const [salesRecords, setSalesRecords] = useState([]);
  const [ledgerPayments, setLedgerPayments] = useState([]);
  const [agentPayments, setAgentPayments] = useState([]);
  const [consolidatedList, setConsolidatedList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search, Filter & Tab States
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All'); // 'All', 'Sales', 'Ledger Payment', 'Agent Payment'

  // Pagination States (15 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 1. Fetch Paid Sales Records
  useEffect(() => {
    const qSales = query(collection(db, 'orderAcceptedbyAgent'), orderBy('createdAt', 'desc'));
    const unsubSales = onSnapshot(qSales, (snapshot) => {
      const records = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          const payment = data.payment || {};
          const rawDate = data.createdAt ? data.createdAt.toDate() : new Date();
          const dateStr = rawDate.toISOString().split('T')[0];

          return {
            id: `sales_${doc.id}`,
            date: dateStr,
            rawDate: rawDate,
            type: 'Sales',
            referenceNo: data.displayOrderId || data.autoGeneratedId || doc.id,
            particulars: data.customerName || data.userName || 'Customer Sale',
            category: 'Income',
            amount: Number(payment.totalPayable) || 0,
            paymentStatus: payment.status || 'Unpaid'
          };
        })
        .filter((item) => item.paymentStatus.toLowerCase() === 'paid');

      setSalesRecords(records);
    });

    return () => unsubSales();
  }, []);

  // 2. Fetch Ledger Payments
  useEffect(() => {
    const qLedger = query(collection(db, 'ledgerPayments'), orderBy('createdAt', 'desc'));
    const unsubLedger = onSnapshot(qLedger, (snapshot) => {
      const records = snapshot.docs.map((doc) => {
        const data = doc.data();
        const dateStr = data.date || new Date().toISOString().split('T')[0];

        return {
          id: `ledger_${doc.id}`,
          date: dateStr,
          rawDate: new Date(dateStr),
          type: 'Ledger Payment',
          referenceNo: data.voucherNo || 'N/A',
          particulars: `${data.expCode || ''} ${data.expName || ''}`.trim() || 'Expense Payment',
          category: 'Expense',
          amount: Number(data.amount) || 0,
        };
      });

      setLedgerPayments(records);
    });

    return () => unsubLedger();
  }, []);

  // 3. Fetch Agent Payments
  useEffect(() => {
    const qAgents = query(collection(db, 'agentsPayments'), orderBy('createdAt', 'desc'));
    const unsubAgents = onSnapshot(qAgents, (snapshot) => {
      const records = snapshot.docs.map((doc) => {
        const data = doc.data();
        const dateStr = data.date || new Date().toISOString().split('T')[0];

        return {
          id: `agent_${doc.id}`,
          date: dateStr,
          rawDate: new Date(dateStr),
          type: 'Agent Payment',
          referenceNo: data.voucherNo || 'N/A',
          particulars: `${data.agentName || ''} (${data.companyName || 'Independent'})`.trim(),
          category: 'Expense',
          amount: Number(data.amount) || 0,
        };
      });

      setAgentPayments(records);
      setLoading(false);
    });

    return () => unsubAgents();
  }, []);

  // 4. Combine and Sort Data
  useEffect(() => {
    const combined = [...salesRecords, ...ledgerPayments, ...agentPayments];
    combined.sort((a, b) => b.rawDate - a.rawDate);

    setConsolidatedList(combined);
    setFilteredList(combined);
  }, [salesRecords, ledgerPayments, agentPayments]);

  // 5. Filter Logic
  useEffect(() => {
    let result = consolidatedList;

    if (categoryFilter !== 'All') {
      result = result.filter((item) => item.type === categoryFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.referenceNo.toLowerCase().includes(term) ||
          item.particulars.toLowerCase().includes(term) ||
          item.type.toLowerCase().includes(term)
      );
    }

    if (startDate) {
      result = result.filter((item) => item.date >= startDate);
    }

    if (endDate) {
      result = result.filter((item) => item.date <= endDate);
    }

    setFilteredList(result);
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate, categoryFilter, consolidatedList]);

  // Overall Financial Metric Calculations
  const totalIncome = salesRecords.reduce((sum, item) => sum + item.amount, 0);
  const totalLedgerExpenses = ledgerPayments.reduce((sum, item) => sum + item.amount, 0);
  const totalAgentExpenses = agentPayments.reduce((sum, item) => sum + item.amount, 0);
  const totalExpenses = totalLedgerExpenses + totalAgentExpenses;
  const availableBalance = totalIncome - totalExpenses;

  // Pagination Calculations
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedList = filteredList.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="consolidated-report-container">
      <div className="consolidated-report-card">
        
        {/* Header */}
        <div className="report-header">
          <div>
            <h2 className="report-title">Consolidated Financial Statement</h2>
            <p className="report-subtitle">Combined ledger statement merging sales revenue and expenditure transactions</p>
          </div>
        </div>

        {/* Top Summary KPI Cards */}
        <div className="report-summary-grid">
          <div className="summary-card total-income-card">
            <div className="card-info">
              <span className="card-label">Total Income (Sales)</span>
              <span className="card-value">AED {totalIncome.toFixed(2)}</span>
            </div>
            <div className="card-icon">💰</div>
          </div>

          <div className="summary-card total-expenses-card">
            <div className="card-info">
              <span className="card-label">Total Expenses (Ledger + Agents)</span>
              <span className="card-value">AED {totalExpenses.toFixed(2)}</span>
            </div>
            <div className="card-icon">💸</div>
          </div>

          <div className={`summary-card balance-card ${availableBalance >= 0 ? 'positive' : 'negative'}`}>
            <div className="card-info">
              <span className="card-label">Available Net Balance</span>
              <span className="card-value">AED {availableBalance.toFixed(2)}</span>
            </div>
            <div className="card-icon">{availableBalance >= 0 ? '📈' : '📉'}</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="report-filter-bar">
          <div className="filter-group main-search">
            <input
              type="text"
              placeholder="Search Reference No, Customer, Ledger, Agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="report-input"
            />
          </div>

          <div className="filter-group type-select">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="report-input select-input"
            >
              <option value="All">All Transaction Types</option>
              <option value="Sales">Sales Revenue Only</option>
              <option value="Ledger Payment">Ledger Expense Payments</option>
              <option value="Agent Payment">Agent Payments</option>
            </select>
          </div>

          <div className="filter-group date-filters">
            <div className="date-field">
              <label>From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="report-input date-input"
              />
            </div>
            <div className="date-field">
              <label>To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="report-input date-input"
              />
            </div>
          </div>
        </div>

        {/* Consolidated Transactions Table */}
        <div className="report-table-wrapper">
          <table className="report-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>S.No</th>
                <th>Date</th>
                <th>Transaction Type</th>
                <th>Ref / Voucher No</th>
                <th>Particulars</th>
                <th style={{ textAlign: 'center' }}>Category</th>
                <th style={{ textAlign: 'right' }}>Amount (AED)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="status-cell">Loading consolidated financial statement...</td>
                </tr>
              ) : currentDisplayedList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="status-cell">No transaction records match the selected filters.</td>
                </tr>
              ) : (
                currentDisplayedList.map((item, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;
                  return (
                    <tr key={item.id}>
                      <td><strong>{serialNumber}</strong></td>
                      <td>{item.date}</td>
                      <td>
                        <span className={`type-badge ${item.type.toLowerCase().replace(/\s+/g, '-')}`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="ref-code-text">{item.referenceNo}</td>
                      <td className="particulars-text">{item.particulars}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`category-tag ${item.category.toLowerCase()}`}>
                          {item.category === 'Income' ? '+ Income' : '- Expense'}
                        </span>
                      </td>
                      <td className={`currency-cell ${item.category.toLowerCase()}`} style={{ textAlign: 'right' }}>
                        {item.category === 'Income' ? '+' : '-'} AED {item.amount.toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loading && filteredList.length > 0 && (
          <div className="report-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredList.length)}</strong> of <strong>{filteredList.length}</strong> transactions
            </div>

            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prev && page - prev > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ConsolidatedReport;